import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { linkToType, ReactLink } from '../Link/Link'
import { Button } from '../Button/Button'
import deprecated from '../../utils/deprecated'

/**
 * Bronson BackToTop component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-back-to-top.html
 *
 * @param {boolean} [button] - Deprecated: Whether rendering a {@link Button} or {@link ReactLink}.
 * @param {string|React.ReactNode} [children] - Additional children.
 * @param {string} [className] - Deprecated: Additional classes on the main element.
 * @param {string} [href] - If {@link button} is not set, this controls the `[href]` to jump to.
 * @param {function} [onClick] - Additional click handler.
 * @param {linkToType} [to] - Deprecated: See {@link linkToType}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The BackToTop component.
 * @constructor
 */
export function BackToTop({ button, children, className, href, onClick, to, testId, ...otherProps }) {
  /**
   * @type {string|(function(): void)}
   */
  const handleClick = onClick || href ? onClick : () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })

  const divClassNameList = classNames('c-back-to-top', 'js-back-to-top', className).trim()

  return (
    <div className={divClassNameList} data-testid={testId} {...otherProps}>
      <div className="c-back-to-top__pagewrap">
        {button ? (
          <Button
            className="js-back-to-top-trigger"
            icon
            iconClassName="c-back-to-top__icon"
            iconViaCss
            link
            simple
            small
            onClick={handleClick}
          >
            {children}
          </Button>
        ) : (
          /**
           * Note: Do not use a {@link Link} as it contains other CSS classes and content structures.
           * We use Button Link Simple’s CSS classes to mimic a {@link Button}.
           * @deprecated Remove `ReactLink`. Removed in https://github.platform.vwfs.io/bronson/bronson/commit/a527fdac7e774120af4a38319f4e98104334cca5.
           */
          <ReactLink
            className="c-btn c-btn--link c-btn--small c-btn--simple js-back-to-top-trigger"
            to={to || href || '#'}
            onClick={handleClick}
          >
            <span className="c-btn__text">{children}</span>
            <i className="c-btn__icon c-back-to-top__icon" aria-hidden="true" role="img" />
          </ReactLink>
        )}
      </div>
    </div>
  )
}

BackToTop.propTypes = {
  button: deprecated(
    PropTypes.bool,
    'Link variant removed in Bronson v9.0.0. Will be removed in Bronson-React v6.0.0.'
  ), // Bronson template: 'button-element'.
  children: PropTypes.node, // Bronson template: 'button-label'.
  className: PropTypes.string,
  href: deprecated(
    PropTypes.string,
    'Link variant removed in Bronson v9.0.0. Will be removed in Bronson-React v6.0.0.'
  ),
  onClick: PropTypes.func, // Added for `[onClick]` attribute.
  to: deprecated(linkToType, 'Link variant removed in Bronson v9.0.0. Will be removed in Bronson-React v6.0.0.'),
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}
